import { Smartphone, RotateCw } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * LandscapePrompt — full-panel friendly prompt shown when a mobile-portrait user
 * opens a matching activity. Auto-dismisses when the user rotates to landscape
 * (parent handles that via useMobileOrientation).
 *
 * Props:
 *  - onContinue: () => void  called when the user taps "Continue Anyway"
 */
const LandscapePrompt = ({ onContinue }) => {
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-mm-parchment to-white border-2 border-mm-navy/10 p-6 sm:p-10 text-center"
      style={{ borderColor: "#1B3A5C33" }}
      data-testid="landscape-prompt"
    >
      {/* Compass logo */}
      <div className="flex justify-center" data-testid="landscape-prompt-logo">
        <Logo size={52} />
      </div>

      {/* Rotating phone illustration */}
      <div className="mt-6 flex justify-center" aria-hidden>
        <div className="relative w-24 h-24 flex items-center justify-center">
          <div
            className="absolute inset-0 rounded-full opacity-20"
            style={{ background: "#D4A843", filter: "blur(20px)" }}
          />
          <div
            className="relative animate-mm-phone-rotate origin-center"
            data-testid="landscape-prompt-phone-icon"
          >
            <Smartphone size={64} strokeWidth={2} color="#1B3A5C" />
          </div>
          <RotateCw
            size={18}
            strokeWidth={3}
            color="#D4A843"
            className="absolute -top-1 -right-1 animate-spin-slow"
            aria-hidden
          />
        </div>
      </div>

      <p
        className="font-heading font-black text-xl sm:text-2xl mt-6 leading-tight"
        style={{ color: "#1B3A5C" }}
        data-testid="landscape-prompt-message"
      >
        This activity works best in landscape mode!
        <br />
        <span className="font-bold text-base sm:text-lg opacity-85">
          Turn your phone sideways for the best matching experience. 🧭
        </span>
      </p>

      <p
        className="text-sm mt-4 opacity-70"
        style={{ color: "#1B3A5C" }}
        data-testid="landscape-prompt-subtext"
      >
        Already sideways? Tap below to continue anyway.
      </p>

      <button
        onClick={onContinue}
        className="btn-trail btn-gold mt-5 text-sm"
        style={{ backgroundColor: "#D4A843" }}
        data-testid="landscape-prompt-continue-btn"
      >
        Continue Anyway
      </button>

      {/* Inline keyframes to avoid touching global CSS */}
      <style>{`
        @keyframes mm-phone-rotate {
          0%   { transform: rotate(0deg); }
          40%  { transform: rotate(-90deg); }
          60%  { transform: rotate(-90deg); }
          100% { transform: rotate(0deg); }
        }
        .animate-mm-phone-rotate {
          animation: mm-phone-rotate 2.8s ease-in-out infinite;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 4s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default LandscapePrompt;
